/**
 * CoinGecko REST layer — cache with TTL, single-flight, 429 backoff,
 * AbortController. No axios: native fetch only.
 */
import { CACHE_TTL } from "../utils/constants";

const BASE = "https://api.coingecko.com/api/v3";
const DEMO_KEY = process.env.REACT_APP_COINGECKO_KEY;

const cache = new Map(); // key -> { expires, data }
const inflight = new Map(); // key -> { promise, controllers }
const backoffUntil = { until: 0 };

export class ApiError extends Error {
  constructor(status, message, retryAfterMs) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }
}

function cacheKey(path, params) {
  const qs = params
    ? Object.keys(params)
        .sort()
        .map((k) => `${k}=${params[k]}`)
        .join("&")
    : "";
  return `${path}?${qs}`;
}

function backoff(retryAfterMs) {
  const base = Number.isFinite(retryAfterMs) && retryAfterMs > 0 ? retryAfterMs : 2000;
  backoffUntil.until = Date.now() + Math.min(base * 2, 8000);
}

export function msUntilBackoffEnds() {
  return Math.max(0, backoffUntil.until - Date.now());
}

export function isBackingOff() {
  return Date.now() < backoffUntil.until;
}

export function __clearCacheForTests() {
  cache.clear();
  inflight.clear();
  backoffUntil.until = 0;
}

async function request(path, { params, ttl = 60_000, signal, force = false } = {}) {
  const key = cacheKey(path, params);

  // Fresh cache hit — no network (skipped when forced).
  const hit = cache.get(key);
  if (!force && hit && hit.expires > Date.now() && !signal?.aborted) {
    return hit.data;
  }

  // Single-flight: identical request already running -> share the promise.
  const existing = inflight.get(key);
  if (existing) {
    if (signal) {
      const onAbort = () => {};
      signal.addEventListener("abort", onAbort, { once: true });
    }
    return existing.promise;
  }

  // Respect 429 backoff window unless the caller forces a retry.
  if (isBackingOff() && !force) {
    throw new ApiError(
      429,
      "Rate limit backoff in effect",
      msUntilBackoffEnds()
    );
  }

  const controller = new AbortController();
  const entry = { promise: null, controllers: [controller] };
  inflight.set(key, entry);

  const qs = params
    ? `?${Object.entries(params)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&")}`
    : "";
  const url = `${BASE}${path}${qs}`;

  const headers = {};
  if (DEMO_KEY) headers["x-cg-demo-api-key"] = DEMO_KEY;

  entry.promise = (async () => {
    try {
      if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });
      const res = await fetch(url, {
        headers,
        signal: controller.signal,
      });

      if (res.status === 429) {
        const retryAfter = Number(res.headers.get("Retry-After")) * 1000;
        backoff(Number.isFinite(retryAfter) ? retryAfter : undefined);
        throw new ApiError(
          429,
          "CoinGecko rate limit hit",
          msUntilBackoffEnds()
        );
      }
      if (!res.ok) {
        throw new ApiError(res.status, `CoinGecko error ${res.status}`);
      }
      const data = await res.json();
      if (ttl > 0) {
        cache.set(key, { expires: Date.now() + ttl, data });
      }
      return data;
    } finally {
      inflight.delete(key);
    }
  })();

  return entry.promise;
}

export function fetchMarkets({ currency, signal, force } = {}) {
  return request("/coins/markets", {
    params: {
      vs_currency: currency,
      order: "market_cap_desc",
      per_page: 50,
      page: 1,
      sparkline: true,
      price_change_percentage: "1h,24h,7d",
    },
    ttl: CACHE_TTL.markets,
    signal,
    force,
  });
}

export function fetchGlobal({ signal, force } = {}) {
  return request("/global", { ttl: CACHE_TTL.global, signal, force });
}

export function fetchMarketChart({ coinId, currency, signal } = {}) {
  return request(`/coins/${coinId}/market_chart`, {
    params: { vs_currency: currency, days: 30 },
    ttl: CACHE_TTL.chart,
    signal,
  });
}

export function fetchSimplePrice({ ids, currency, signal } = {}) {
  return request("/simple/price", {
    params: {
      ids: Array.isArray(ids) ? ids.join(",") : ids,
      vs_currencies: currency,
      include_24hr_change: true,
    },
    ttl: CACHE_TTL.price,
    signal,
  });
}
